import { Injectable, Inject } from '@angular/core';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { AuthService } from './auth.service';
import { AdminService } from '../../../shared/services/admin/admin.service';
import { AUTH_CONSTANTS } from '../constants/auth.constants';
import { AuthUtils } from '../../../shared/utils/auth.utils';
import { BehaviorSubject } from 'rxjs';

export interface AppInitializationState {
  isTokenInitialized: boolean;
  isAuthCheckComplete: boolean;
  isAuthenticated: boolean;
  isCallbackLoading: boolean;
  loadingMessage: string;
  currentUserName: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppInitializationService {
  private readonly _state = new BehaviorSubject<AppInitializationState>({
    isTokenInitialized: false,
    isAuthCheckComplete: false,
    isAuthenticated: false,
    isCallbackLoading: false,
    loadingMessage: AUTH_CONSTANTS.MESSAGES.VERIFYING_ACCESS,
    currentUserName: ''
  });

  public readonly state$ = this._state.asObservable();

  constructor(
    private adminService: AdminService,
    public authStateService: OktaAuthStateService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private authService: AuthService
  ) { }

  get currentState(): AppInitializationState {
    return this._state.value;
  }

  private updateState(partialState: Partial<AppInitializationState>): void {
    this._state.next({ ...this.currentState, ...partialState });
  }

  /**
   * Initialize the application with authentication and token setup
   */
  async initializeApp(): Promise<void> {
    // Check if we're on callback route immediately
    if (AuthUtils.isCallbackRoute()) {
      this.updateState({
        isCallbackLoading: true,
        loadingMessage: AUTH_CONSTANTS.MESSAGES.PROCESSING_SIGNIN
      });
    }

    // Start both auth check and token initialization simultaneously
    this.initializeAuthToken();
    await this.checkAuthenticationState();
  }

  /**
   * Handle user login
   */
  async login(): Promise<void> {
    await this.oktaAuth.signInWithRedirect({ originalUri: AUTH_CONSTANTS.ROUTES.ROOT });
  }

  /**
   * Update user name when profile changes
   */
  updateUserName(userProfile: any): void {
    const userName = AuthUtils.getUserDisplayName(userProfile);
    this.updateState({ currentUserName: userName });
  }

  /**
   * Check authentication state with comprehensive error handling
   */
  private async checkAuthenticationState(): Promise<void> {
    // Add a timeout to prevent infinite loading
    const authTimeout: ReturnType<typeof setTimeout> = setTimeout(() => {
      if (!this.currentState.isAuthCheckComplete) {
        this.updateState({
          loadingMessage: AUTH_CONSTANTS.MESSAGES.AUTH_TIMEOUT,
          isAuthCheckComplete: true,
          isAuthenticated: false
        });

        // Redirect to login after timeout
        setTimeout(async () => {
          try {
            await this.oktaAuth.signInWithRedirect();
          } catch (error) {
            window.location.reload();
          }
        }, AUTH_CONSTANTS.TIMEOUTS.ERROR_MESSAGE_DELAY);
      }
    }, AUTH_CONSTANTS.TIMEOUTS.AUTH_CHECK);

    try {
      // Handle callback route with loading state
      if (AuthUtils.isCallbackRoute()) {
        await this.handleLoginCallback(authTimeout);
        return;
      }
      await this.handleOktaAuthCheck(authTimeout);

    } catch (error: any) {
      clearTimeout(authTimeout);
      await this.handleAuthError(error);
    }
  }

  /**
   * Handle login callback processing
   */
  private async handleLoginCallback(authTimeout: ReturnType<typeof setTimeout>): Promise<void> {
    this.updateState({
      isCallbackLoading: true,
      loadingMessage: AUTH_CONSTANTS.MESSAGES.COMPLETING_SIGNIN
    });

    try {
      // Wait for Okta to complete token exchange
      await this.oktaAuth.handleLoginRedirect();

      // Load user profile after successful login
      this.updateState({ loadingMessage: AUTH_CONSTANTS.MESSAGES.LOADING_PROFILE });
      await this.authService.loadUserProfileOptimized();

      await AuthUtils.delay(AUTH_CONSTANTS.TIMEOUTS.PROFILE_LOAD_DELAY);

      // Show welcome message with user name
      if (this.currentState.currentUserName) {
        this.updateState({ loadingMessage: AUTH_CONSTANTS.MESSAGES.SETTING_UP_WORKSPACE });
        await AuthUtils.delay(AUTH_CONSTANTS.TIMEOUTS.EXTENDED_WELCOME_DURATION);
      }

      this.updateState({ loadingMessage: AUTH_CONSTANTS.MESSAGES.ALMOST_READY });
      await AuthUtils.delay(AUTH_CONSTANTS.TIMEOUTS.FINAL_SETUP_DELAY);

    } catch (error: any) {
      this.updateState({ loadingMessage: AUTH_CONSTANTS.MESSAGES.AUTH_ERROR });
      await AuthUtils.delay(AUTH_CONSTANTS.TIMEOUTS.ERROR_MESSAGE_DELAY);

      // Clear any partial authentication state and redirect back to login
      this.oktaAuth.tokenManager.clear();
      await this.oktaAuth.signInWithRedirect();
      return;
    }

    clearTimeout(authTimeout);
    this.updateState({
      isAuthCheckComplete: true,
      isAuthenticated: true,
      isCallbackLoading: false
    });
  }

  /**
   * Handle cached user authentication
   */
  private async handleCachedUser(authTimeout: ReturnType<typeof setTimeout>): Promise<void> {
    this.updateState({ loadingMessage: AUTH_CONSTANTS.MESSAGES.LOADING_PROFILE });
    await this.authService.loadUserProfileOptimized();

    await AuthUtils.delay(AUTH_CONSTANTS.TIMEOUTS.PROFILE_LOAD_DELAY);

    if (this.currentState.currentUserName) {
      this.updateState({ loadingMessage: AUTH_CONSTANTS.MESSAGES.PREPARING_DASHBOARD });
      await AuthUtils.delay(AUTH_CONSTANTS.TIMEOUTS.WELCOME_MESSAGE_DURATION);
    }

    this.updateState({ loadingMessage: AUTH_CONSTANTS.MESSAGES.LOADING_APPLICATION });
    await AuthUtils.delay(AUTH_CONSTANTS.TIMEOUTS.SETUP_DELAY);

    clearTimeout(authTimeout);
    this.updateState({
      isAuthenticated: true,
      isAuthCheckComplete: true
    });
  }

  /**
   * Handle Okta authentication check
   */
  private async handleOktaAuthCheck(authTimeout: ReturnType<typeof setTimeout>): Promise<void> {
    this.updateState({ loadingMessage: AUTH_CONSTANTS.MESSAGES.VERIFYING_ACCESS });
    try {
      const isAuthenticated = await this.oktaAuth.isAuthenticated();

      if (!isAuthenticated) {
        this.updateState({ loadingMessage: AUTH_CONSTANTS.MESSAGES.REDIRECTING_SIGNIN });
        await AuthUtils.delay(AUTH_CONSTANTS.TIMEOUTS.REDIRECT_MESSAGE_DELAY);
        clearTimeout(authTimeout);
        await this.oktaAuth.signInWithRedirect();
        return;
      }

      // Load user profile after authentication
      this.updateState({ loadingMessage: AUTH_CONSTANTS.MESSAGES.LOADING_PROFILE });
      const profileLoaded = await this.authService.loadUserProfileOptimized();
      // If profile loading failed (e.g., revoked token), redirect to login
      if (!profileLoaded) {
        this.updateState({
          loadingMessage: AUTH_CONSTANTS.MESSAGES.REDIRECTING_SIGNIN,
          isAuthCheckComplete: true,
          isAuthenticated: false
        });
        clearTimeout(authTimeout);
        await AuthUtils.delay(AUTH_CONSTANTS.TIMEOUTS.REDIRECT_MESSAGE_DELAY);
        await this.oktaAuth.signInWithRedirect();
        return;
      }
      await AuthUtils.delay(AUTH_CONSTANTS.TIMEOUTS.PROFILE_LOAD_DELAY);

      if (this.currentState.currentUserName) {
        this.updateState({ loadingMessage: AUTH_CONSTANTS.MESSAGES.WELCOME_BACK });
        await AuthUtils.delay(AUTH_CONSTANTS.TIMEOUTS.WELCOME_MESSAGE_DURATION);
      }
      this.updateState({ loadingMessage: AUTH_CONSTANTS.MESSAGES.FINALIZING_SETUP });
      await AuthUtils.delay(AUTH_CONSTANTS.TIMEOUTS.SETUP_DELAY);

      clearTimeout(authTimeout);
      this.updateState({
        isAuthCheckComplete: true,
        isAuthenticated: true
      });
    } catch (authCheckError: any) {
      clearTimeout(authTimeout);
      // If authentication check itself fails, redirect to login
      this.updateState({
        loadingMessage: AUTH_CONSTANTS.MESSAGES.AUTH_FAILED,
        isAuthCheckComplete: true,
        isAuthenticated: false
      });
      await AuthUtils.delay(AUTH_CONSTANTS.TIMEOUTS.ERROR_MESSAGE_DELAY);
      await this.oktaAuth.signInWithRedirect();
    }
  }

  /**
   * Handle authentication errors
   */
  private async handleAuthError(error: any): Promise<void> {
    // Handle network errors with retry
    if (AuthUtils.isNetworkError(error)) {
      this.updateState({ loadingMessage: AUTH_CONSTANTS.MESSAGES.NETWORK_ERROR });
      setTimeout(() => {
        this.checkAuthenticationState();
      }, AUTH_CONSTANTS.TIMEOUTS.NETWORK_RETRY_DELAY);
      return;
    }

    // For other auth errors, clear the loading state and redirect to login
    this.updateState({
      loadingMessage: AUTH_CONSTANTS.MESSAGES.AUTH_FAILED,
      isAuthCheckComplete: true,
      isAuthenticated: false
    });

    setTimeout(async () => {
      try {
        await this.oktaAuth.signInWithRedirect();
      } catch (redirectError) {
        window.location.reload();
      }
    }, AUTH_CONSTANTS.TIMEOUTS.ERROR_MESSAGE_DELAY);
  }

  /**
   * Initialize authentication token
   */
  private initializeAuthToken(): void {
    const existingToken = AuthUtils.getValidApiToken();

    if (!existingToken) {
      this.adminService.postLoginTokenForCMS().subscribe({
        next: (res) => {
          AuthUtils.storeApiToken(res.Token);
          this.updateState({ isTokenInitialized: true });
        },
        error: (error) => {
          this.updateState({ isTokenInitialized: true }); // Set to true even on error to avoid infinite loading
        }
      });
    } else {
      this.updateState({ isTokenInitialized: true });
    }
  }
}
